import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Snackbar, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded';
import { AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react';
import { analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import { getAppStyleTokens } from '../../styles/msiStyleTokens';
import DataTable from '../data_table/DataTable';
import KitTooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';
import DraftFreshnessHeaderControl from '../pos_sizing_table/DraftFreshnessHeaderControl';
import OptimizerSavedSecuritiesTable from './OptimizerSavedSecuritiesTable';
import { OptimizerRecoveryAction, OptimizerZeroOverlapWarning, OptimizerWatchlistRestorationPreview } from './OptimizerWatchlistRestorationDialog';
import { formatBaselineLabel, formatRelativeAge, formatOptimizerContextDetail, portfolioInfoKey } from './optimizerPortfolioPresentation';


const RESTORATION_PREVIEW_FRAME_CLASS_NAME = 'max-h-[162px] '
    + 'sm:max-h-[clamp(162px,calc(100dvh_-_650px),322px)]';

const CONTEXT_STATUS = Object.freeze({
    Current: {
        Icon: CheckCircle2,
        tooltip: 'Matches the saved input',
        className: 'text-ui-success',
    },
    Stale: {
        Icon: AlertCircle,
        tooltip: 'Changed since saved',
        className: 'text-ui-warning',
    },
    Unknown: {
        Icon: HelpCircle,
        tooltip: 'Not verified',
        className: 'text-content-secondary',
    },
});

const getChangeRowId = (row) => row.key;

const CONTEXT_COLUMNS = Object.freeze([
    { id: 'label', header: 'Input', accessorFn: (row) => row.label, enableSorting: false },
    { id: 'savedValue', header: 'Saved With', accessorFn: (row) => row.savedValue, enableSorting: false },
    { id: 'currentValue', header: 'Now', accessorFn: (row) => row.currentValue, enableSorting: false },
    {
        id: 'status',
        header: 'Status',
        size: 44,
        enableSorting: false,
        cell: ({ row: { original } }) => {
            const status = CONTEXT_STATUS[original.status] ?? CONTEXT_STATUS.Unknown;
            const label = CONTEXT_STATUS[original.status] ? original.status : 'Unknown';
            return (
                <KitTooltip content={status.tooltip}>
                    <status.Icon
                        role="img"
                        aria-label={label}
                        aria-hidden={false}
                        size={18}
                        strokeWidth={2.25}
                        className={cx('inline-block align-middle', status.className)}
                    />
                </KitTooltip>
            );
        },
        meta: { align: 'center', wrap: true },
    },
]);



export default function SavedPortfolioDialogs({ model, actions, loading, nowMs, allowDelete, restrictedDeletePortfolioNames, draft }) {
    const { confirmOpen, itemToDelete, notification, refreshIssueItem, infoItem, infoDetail, currentInfoItem,
        infoSummaryIsCurrent, infoItemContextPresentation, infoStatusTag, infoChangeRows, infoItemMembershipDelta,
        canRestoreInfoItem, restoration, restorationDisabled, recoveryUnavailableReason, infoRecoveryRequired,
        infoRecoveryChecking, canReoptimizeInfoItem, reoptimization, infoReoptimization, savedInputReplay } = model;
    const { confirmDelete: handleConfirmDelete, reoptimize: handleReoptimize,
        replaySavedInputs: handleReplaySavedInputs, openInfoItem, loadInfoDetail } = actions;
    const { posSizingFreshnessSummary, onSyncPosSizingDraft, posSizingSyncDisabled, posSizingSyncInProgress } = draft;
    return (
        <>
            <Dialog open={confirmOpen} onClose={actions.cancelDelete}>
                <DialogTitle>Delete portfolio?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{itemToDelete?.value}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={actions.cancelDelete} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={loading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <DraftFreshnessHeaderControl
                summary={posSizingFreshnessSummary ?? { visible: false }}
                onSyncClick={onSyncPosSizingDraft}
                syncDisabled={posSizingSyncDisabled}
                syncInProgress={posSizingSyncInProgress}
                detailsOpen={infoItem?.type === 'wi'}
                onDetailsOpenChange={actions.setDraftDetailsOpen}
                triggerVariant="dialog-only"
            />

            <Dialog
                open={Boolean(infoItem?.type === 'opt')}
                onClose={actions.closeDetails}
                aria-labelledby="optimizer-portfolio-details-title"
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { maxWidth: 820 } }}
            >
                <DialogTitle component="div" id="optimizer-portfolio-details-header"
                    sx={{ borderBottom: 0, pb: 1.5, pr: 6 }}>
                    <Typography id="optimizer-portfolio-details-title" component="h2"
                        sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25, mb: 0.5 }}>
                        {infoItem?.value || 'Optimized Portfolio'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            Based on {formatBaselineLabel(infoItem?.baselineSourceMode)} Portfolio
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }} aria-hidden="true">
                            ·
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            Saved {formatRelativeAge(infoItem?.lastRefreshedAt, nowMs)}
                        </Typography>
                        {[infoItem?.factorModel, infoItem?.factorDataModel, infoItem?.factorSnapshotDate]
                            .filter(Boolean).length > 0 && <Typography variant="caption" color="text.secondary">
                            {[infoItem?.factorModel, infoItem?.factorDataModel, infoItem?.factorSnapshotDate]
                                .filter(Boolean).join(' · ')}
                        </Typography>}
                        {infoStatusTag && !infoRecoveryRequired && (
                            <Chip
                                size="small"
                                color={infoStatusTag.color}
                                label={infoStatusTag.label}
                                sx={{ ml: 'auto' }}
                            />
                        )}
                    </Box>
                    <IconButton aria-label="Close portfolio details" disabled={restoration.staging}
                        onClick={actions.closeDetails}
                        sx={{ position: 'absolute', right: 16, top: 16, width: 28, height: 28, border: 0 }}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 2 }}>
                    <Stack spacing={1.5}>

                        {infoRecoveryRequired ? (
                            <Alert severity="warning" sx={{ py: 0.5, alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>
                                    Some saved securities are no longer in your Watchlist.
                                </Typography>
                                <Typography variant="caption">
                                    Recover the missing Watchlist securities before re-optimizing.
                                </Typography>
                            </Alert>
                        ) : infoSummaryIsCurrent && <Typography variant="body2" color="text.secondary">
                            {formatOptimizerContextDetail(
                                infoItemContextPresentation,
                            )}
                        </Typography>}

                        {infoItemMembershipDelta && (
                            <Box>
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Missing securities
                                    </Typography>
                                    <Chip size="small"
                                        label={`${infoItemContextPresentation.missingSecurityCount} missing securit${infoItemContextPresentation.missingSecurityCount === 1 ? 'y' : 'ies'}`}
                                        sx={theme => ({
                                            height: 21, fontSize: 11, fontWeight: 600, textTransform: 'none',
                                            color: getAppStyleTokens(theme).palette.info,
                                            backgroundColor: getAppStyleTokens(theme).palette.infoBg,
                                            border: 0,
                                        })} />
                                </Stack>
                                {canRestoreInfoItem ? (
                                    <OptimizerWatchlistRestorationPreview
                                        preview={restoration.preview}
                                        loading={restoration.loading}
                                        dialogError={restoration.dialogError}
                                        className={RESTORATION_PREVIEW_FRAME_CLASS_NAME}
                                    />
                                ) : <Box
                                    sx={[{
                                        border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.45)}`,
                                        borderRadius: 1.5,
                                        px: 1.25,
                                        py: 1,
                                        backgroundColor: theme => alpha(theme.palette.warning.main, 0.07),
                                    }, analyticsPanelSx]}
                                >
                                    <Stack spacing={0.5}>
                                        {infoItemMembershipDelta.summaryParts?.map((part, idx) => (
                                            <Typography key={idx} variant="body2">
                                                {part.label}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Box>}
                            </Box>
                        )}

                        {infoRecoveryRequired && (
                            <Box sx={theme => ({ p: 1.5,
                                border: `1px solid ${getAppStyleTokens(theme).palette.line}`,
                                borderRadius: '6px', backgroundColor: getAppStyleTokens(theme).palette.surfaceSubtle })}>
                                {!infoItemMembershipDelta && restoration.dialogError ? <Alert severity="error" sx={{ mb: 1 }}>{restoration.dialogError}</Alert> : null}
                                <OptimizerRecoveryAction restoration={restoration} disabled={restorationDisabled || !canRestoreInfoItem}
                                    disabledReason={recoveryUnavailableReason} />
                                {restoration.staging && <Typography role="status" variant="caption" display="block" sx={{ mt: 1 }}>Preparing recovery…</Typography>}
                            </Box>
                        )}

                        {infoRecoveryChecking && (
                            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">
                                    Checking the saved portfolio…
                                </Typography>
                            </Box>
                        )}

                        {!infoRecoveryRequired && infoSummaryIsCurrent && infoChangeRows.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                                    Optimization context
                                </Typography>
                                <DataTable
                                    aria-label="Saved and current optimization inputs"
                                    data={infoChangeRows}
                                    columns={CONTEXT_COLUMNS}
                                    getRowId={getChangeRowId}
                                    tableLayout="auto"
                                    rowHeight={32}
                                    headerHeight={34}
                                />
                            </Box>
                        )}
                        {!infoSummaryIsCurrent ? <Alert severity="warning" action={
                            <Button color="inherit" disabled={!currentInfoItem}
                                onClick={() => openInfoItem(currentInfoItem)}>Refresh details</Button>
                        }>The saved portfolio changed. Refresh its details.</Alert> : infoDetail?.key === portfolioInfoKey(infoItem) && (
                            infoDetail.loading ? <OptimizerSavedSecuritiesTable
                                key={infoDetail.key} /> : infoDetail.unavailable ? <Alert severity="warning" action={
                                <Button color="inherit" onClick={() => loadInfoDetail(infoItem, {
                                    listStale: infoDetail.listStale,
                                })}>Retry</Button>
                            }>Saved securities are unavailable.</Alert> : <OptimizerSavedSecuritiesTable
                                key={infoDetail.key} detail={infoDetail.detail} savedAum={infoDetail.savedAum} />
                        )}
                        {!infoRecoveryRequired && !infoRecoveryChecking && canReoptimizeInfoItem && (
                            <Box sx={theme => ({ p: 1.5,
                                border: `1px solid ${getAppStyleTokens(theme).palette.line}`,
                                borderRadius: '6px', backgroundColor: getAppStyleTokens(theme).palette.surfaceSubtle })}>
                                <Stack spacing={1} sx={{ maxWidth: 620, mx: 'auto' }}>
                                    {restoration.dialogError && <Alert severity="warning">{restoration.dialogError}</Alert>}
                                    {restoration.preview?.current_baseline_overlap_count === 0 && (
                                        <OptimizerZeroOverlapWarning
                                            baselineSourceMode={restoration.preview.baseline_source_mode} />
                                    )}
                                    {infoReoptimization?.status === 'error' && (
                                        <Alert severity="error">{infoReoptimization.message}</Alert>
                                    )}
                                    <Button fullWidth variant="contained"
                                        disabled={loading || reoptimization?.status === 'running'}
                                        onClick={handleReoptimize}
                                        startIcon={infoReoptimization?.status === 'running'
                                            ? <CircularProgress size={14} color="inherit" />
                                            : <RefreshRoundedIcon />}>
                                        {infoReoptimization?.status === 'running' ? 'Re-optimizing…' : 'Re-optimize'}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                        Uses the saved selection and limits with current
                                        {' '}{formatBaselineLabel(infoItem?.baselineSourceMode)} inputs.
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        {infoItem?.type === 'opt' && savedInputReplay.result && (
                            <Alert
                                severity={savedInputReplay.result.status === 'matched' ? 'success' : 'warning'}
                            >
                                {savedInputReplay.result.status === 'matched'
                                    ? 'The saved inputs reproduced the saved optimizer result using the current compatible runtime.'
                                    : savedInputReplay.result.status === 'different'
                                        ? 'The saved inputs completed, but the current runtime produced a different result. The saved receipt remains authoritative for the original run.'
                                        : savedInputReplay.result.failure?.message}
                            </Alert>
                        )}
                        {infoItem?.type === 'opt' && savedInputReplay.error && (
                            <Alert severity="error">{savedInputReplay.error}</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={theme => ({
                    px: 2.5, py: 1.25, gap: 1, flexWrap: 'wrap',
                    borderTop: `1px solid ${getAppStyleTokens(theme).palette.line}`,
                })}>
                    {infoItem?.type === 'opt' && allowDelete && !restrictedDeletePortfolioNames.includes(infoItem.value) && (
                        <Button color="error" startIcon={<DeleteIcon />}
                            disabled={loading || restoration.staging || infoReoptimization?.status === 'running'}
                            onClick={actions.deleteFromDetails} sx={theme => ({
                                mr: 'auto', color: getAppStyleTokens(theme).palette.danger,
                                '&:hover': { backgroundColor: getAppStyleTokens(theme).palette.dangerBg },
                            })}>
                            Delete optimized portfolio
                        </Button>
                    )}
                    {infoItem?.type === 'opt'
                        && !infoItemContextPresentation?.restorationRequired
                        && !canRestoreInfoItem
                        && infoItem?.contextStatus?.actions?.replay_saved_inputs
                            ?.availability === 'preflight_required' && (
                        <Button
                            onClick={handleReplaySavedInputs}
                            disabled={savedInputReplay.loading}
                            startIcon={savedInputReplay.loading
                                ? <CircularProgress size={14} />
                                : <HistoryToggleOffRoundedIcon />}
                        >
                            Check saved run reproducibility
                        </Button>
                    )}
                    {infoRecoveryRequired ? (
                        <Typography variant="caption" color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 380, fontSize: 11, textAlign: 'right' }}>
                            Recovery adds missing Watchlist securities. Re-optimize is a separate action.
                        </Typography>
                    ) : null}
                    <Button disabled={restoration.staging} onClick={actions.closeDetails}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(refreshIssueItem)}
                onClose={actions.closeRefreshIssue}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {refreshIssueItem?.portfolioName
                        ? `${refreshIssueItem.portfolioName}: Action failed`
                        : 'Portfolio action failed'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        <Typography variant="body2">
                            {refreshIssueItem?.details || 'The portfolio action could not be completed.'}
                        </Typography>
                        {refreshIssueItem?.portfolioType === 'wi' && (
                            <Typography variant="body2" color="text.secondary">
                                Retry the draft refresh. Opening Position Sizing is optional and is not required to initialize the draft. If retry still fails, review the Actual portfolio and current context evidence before syncing again.
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={actions.closeRefreshIssue}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={actions.dismissNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={actions.dismissNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}
